// TelcoTrack — shared client-side sort for `app-data-table`.

import type { DataTableColumn, DataTableSortOrder } from './data-table.types';

/**
 * Columns are sortable by default.
 * Opt out with `sortable: false`, or via reserved ids (`actions` / `select` / `expand`)
 * when `sortable` is omitted.
 */
export function isDataTableColumnSortable<T>(column: DataTableColumn<T>): boolean {
  if (column.sortable === false) return false;
  if (column.sortable === true) return true;
  const id = (column.id ?? '').trim().toLowerCase();
  if (id === 'actions' || id === 'select' || id === 'expand') return false;
  return true;
}

/**
 * Resolve the comparable value for a column.
 * Prefer `sortValue`, then `format`, then `row[column.id]`.
 */
export function getDataTableSortValue<T>(row: T, column: DataTableColumn<T>): unknown {
  if (column.sortValue) {
    return column.sortValue(row);
  }
  if (column.format) {
    return column.format(row);
  }
  if (row != null && typeof row === 'object' && column.id in (row as object)) {
    return (row as Record<string, unknown>)[column.id];
  }
  return null;
}

function normalizeSortScalar(value: unknown): string | number | null {
  if (value == null) return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed === '—') return null;
    // Prefer numeric compare when the displayed cell is a plain number.
    if (/^-?\d+([.,]\d+)?$/.test(trimmed)) {
      const n = Number(trimmed.replace(',', '.'));
      if (Number.isFinite(n)) return n;
    }
    // ISO / locale-ish dates → timestamp when parseable.
    const asDate = Date.parse(trimmed);
    if (!Number.isNaN(asDate) && /\d{4}-\d{2}-\d{2}|\d{1,2}\.\d{1,2}\.\d{4}/.test(trimmed)) {
      return asDate;
    }
    return trimmed.toLocaleLowerCase('cs-CZ');
  }
  if (typeof value === 'object') {
    return String(value).toLocaleLowerCase('cs-CZ');
  }
  return String(value);
}

/** Compare two non-null sort scalars. */
export function compareDataTableSortValues(a: unknown, b: unknown): number {
  const na = normalizeSortScalar(a);
  const nb = normalizeSortScalar(b);
  if (na == null && nb == null) return 0;
  if (na == null) return 1;
  if (nb == null) return -1;
  if (typeof na === 'number' && typeof nb === 'number') {
    return na === nb ? 0 : na < nb ? -1 : 1;
  }
  return String(na).localeCompare(String(nb), 'cs-CZ', {
    numeric: true,
    sensitivity: 'base',
  });
}

/**
 * Stable client-side sort of in-memory rows by column id.
 * Empty/null values stay last in both directions.
 * Unknown / non-sortable columns leave order unchanged.
 */
export function sortDataTableRows<T>(
  rows: readonly T[],
  columns: readonly DataTableColumn<T>[],
  sortBy: string,
  sortOrder: DataTableSortOrder = 'asc',
): T[] {
  const columnId = (sortBy ?? '').trim();
  if (!columnId || rows.length < 2) {
    return rows.slice();
  }
  const column = columns.find((c) => c.id === columnId);
  if (!column || !isDataTableColumnSortable(column)) {
    return rows.slice();
  }
  const dir = sortOrder === 'desc' ? -1 : 1;
  return rows
    .map((row, index) => {
      const value = getDataTableSortValue(row, column);
      return { row, index, value, normalized: normalizeSortScalar(value) };
    })
    .sort((a, b) => {
      if (a.normalized == null && b.normalized == null) return a.index - b.index;
      if (a.normalized == null) return 1;
      if (b.normalized == null) return -1;
      const cmp = compareDataTableSortValues(a.value, b.value);
      if (cmp !== 0) return cmp * dir;
      return a.index - b.index;
    })
    .map((entry) => entry.row);
}

/** Slice a full (already sorted/filtered) list for the current page. */
export function sliceDataTablePage<T>(
  rows: readonly T[],
  currentPage: number,
  itemsPerPage: number,
  showAll = false,
): T[] {
  if (showAll || itemsPerPage <= 0) {
    return rows.slice();
  }
  const page = Math.max(1, currentPage || 1);
  const start = (page - 1) * itemsPerPage;
  return rows.slice(start, start + itemsPerPage);
}
